import { useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../utils/constant';

/**
 * Connects to the admin SSE notification stream and calls the provided
 * callbacks whenever the server pushes an event.
 *
 * Falls back to a 30-second polling interval when:
 *  - The browser does not support EventSource
 *  - The SSE connection fails to open after MAX_RETRY_MS
 *
 * @param {Object}   callbacks
 * @param {Function} callbacks.onNotification  – called with { notification, counts }
 * @param {Function} callbacks.onCounts        – called with { unread, critical }
 * @param {Function} [callbacks.onFallbackPoll] – called when the hook falls back to polling; return a promise
 */
export default function useNotificationStream({ onNotification, onCounts, onFallbackPoll }) {
  const sourceRef    = useRef(null);
  const retryTimer   = useRef(null);
  const fallbackTimer = useRef(null);
  const retryCount   = useRef(0);
  const MAX_RETRIES  = 5;
  // Exponential back-off: 2s, 4s, 8s, 16s, 30s cap
  const retryDelay   = () => Math.min(2000 * 2 ** retryCount.current, 30_000);

  const startFallbackPolling = useCallback(() => {
    if (!onFallbackPoll) return;
    clearInterval(fallbackTimer.current);
    fallbackTimer.current = setInterval(onFallbackPoll, 30_000);
    onFallbackPoll(); // immediate first call
  }, [onFallbackPoll]);

  const connect = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // EventSource cannot set Authorization headers — use ?_token query param,
    // which the backend adminMiddleware already accepts.
    const url = `${API_URL}/notification/stream?_token=${encodeURIComponent(token)}`;

    if (!window.EventSource) {
      startFallbackPolling();
      return;
    }

    const source = new window.EventSource(url);
    sourceRef.current = source;

    source.addEventListener('connected', (e) => {
      retryCount.current = 0;
      try { onCounts(JSON.parse(e.data)); } catch (_) { /* parse error ignored */ }
    });

    source.addEventListener('notification', (e) => {
      try { onNotification(JSON.parse(e.data)); } catch (_) { /* parse error ignored */ }
    });

    source.addEventListener('counts', (e) => {
      try { onCounts(JSON.parse(e.data)); } catch (_) { /* parse error ignored */ }
    });

    // heartbeat — no-op, keeps the connection counted as alive
    source.addEventListener('heartbeat', () => {});

    source.onerror = () => {
      source.close();
      sourceRef.current = null;

      if (retryCount.current >= MAX_RETRIES) {
        startFallbackPolling();
        return;
      }

      const delay = retryDelay();
      retryCount.current += 1;
      retryTimer.current = setTimeout(connect, delay);
    };
  }, [onNotification, onCounts, startFallbackPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(retryTimer.current);
      clearInterval(fallbackTimer.current);
      if (sourceRef.current) {
        sourceRef.current.close();
        sourceRef.current = null;
      }
    };
  }, [connect]);
}
